import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Node, mergeAttributes, type JSONContent } from "@tiptap/core";
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
  type NodeViewProps,
} from "@tiptap/react";
import { viewModes } from "./view-modes";
import StarterKit from "@tiptap/starter-kit";
import {
  Circle,
  CircleCheck,
  UserRound,
  FileText,
  PanelsTopLeft,
} from "lucide-react";
export interface ChatReference {
  kind: "task" | "member" | "view" | "document";
  id: string;
  label: string;
  type?: string;
  status?: string;
  isShared?: boolean;
  projectLabel?: string;
}
function ReferenceChip({ node }: NodeViewProps) {
  const Icon =
    node.attrs.kind === "task"
      ? node.attrs.status === "DONE"
        ? CircleCheck
        : Circle
      : node.attrs.kind === "member"
        ? UserRound
        : node.attrs.kind === "document"
          ? FileText
          : (viewModes.find((mode) => mode.type === node.attrs.viewType)
              ?.icon ?? PanelsTopLeft);
  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-sky-400/20 bg-sky-400/10 px-1.5 align-baseline text-sm text-sky-300"
    >
      <Icon className="inline h-3 w-3" />
      {node.attrs.label}
      {node.attrs.projectLabel && <span className="text-xs opacity-70">· {node.attrs.projectLabel}</span>}
    </NodeViewWrapper>
  );
}
const Reference = Node.create({
  name: "chatReference",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes: () => ({
    kind: { default: "task" },
    id: { default: "" },
    label: { default: "" },
    viewType: { default: "" },
    status: { default: "" },
    projectLabel: { default: "" },
  }),
  parseHTML: () => [{ tag: "span[data-chat-reference]" }],
  renderHTML: ({ node, HTMLAttributes }) => [
    "span",
    mergeAttributes(HTMLAttributes, { "data-chat-reference": true }),
    node.attrs.label,
  ],
  renderText: ({ node }) => node.attrs.label,
  addNodeView: () => ReactNodeViewRenderer(ReferenceChip),
});
function content(value: string) {
  const nodes: Record<string, unknown>[] = [];
  const regex = /@\[([^\]]+)\]\((task|member|view|document):([^\s)]+)\)/g;
  let at = 0;
  let match;
  while ((match = regex.exec(value))) {
    if (match.index > at)
      nodes.push({ type: "text", text: value.slice(at, match.index) });
    nodes.push({
      type: "chatReference",
      attrs: { label: match[1], kind: match[2], id: match[3] },
    });
    at = regex.lastIndex;
  }
  if (at < value.length) nodes.push({ type: "text", text: value.slice(at) });
  return { type: "doc", content: [{ type: "paragraph", content: nodes }] };
}
function serialize(node: JSONContent): string {
  if (node.type === "chatReference")
    return `@[${String(node.attrs?.label).replace(/[\[\]\n]/g, " ")}](${node.attrs?.kind}:${node.attrs?.id})`;
  if (node.type === "text") return node.text ?? "";
  if (node.type === "hardBreak") return "\n";
  return (node.content ?? [])
    .map(serialize)
    .join(node.type === "doc" ? "\n" : "");
}
export interface ReferenceComposerHandle {
  insert(reference: ChatReference, replaceMention?: boolean): void;
}
export interface MentionAnchor { left: number; top: number; bottom: number }
export const ReferenceComposer = forwardRef<
  ReferenceComposerHandle,
  {
    value: string;
    onChange(value: string): void;
    onMention(query: string | null, anchor?: MentionAnchor): void;
    onMentionKey?(event: KeyboardEvent): boolean;
    onSend(): void;
    disabled: boolean;
    placeholder: string;
  }
>(function ReferenceComposer(
  { value, onChange, onMention, onMentionKey, onSend, disabled, placeholder },
  ref,
) {
  const mentionRange = useRef<{ from: number; to: number } | null>(null);
  const dismissed = useRef<string | null>(null);
  const updateMention = (current: import("@tiptap/core").Editor) => {
    if (current.view.composing) return;
    const selection = current.state.selection;
    const prefix = selection.$from.parent.textBetween(0, selection.$from.parentOffset, "\n", "\uFFFC");
    const match = selection.empty ? prefix.match(/(?:^|\s)@([^@\n\uFFFC]*)$/) : null;
    const key = match ? `${selection.from}:${match[1]}` : null;
    if (!match || dismissed.current === key) {
      mentionRange.current = null;
      onMention(null);
      return;
    }
    dismissed.current = null;
    mentionRange.current = { from: selection.from - match[1]!.length - 1, to: selection.from };
    const coords = current.view.coordsAtPos(selection.from);
    onMention(match[1]!, { left: coords.left, top: coords.top, bottom: coords.bottom });
  };
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Reference,
    ],
    content: content(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-autocomplete": "list",
        "aria-controls": "chat-inline-references",
        "aria-label": placeholder,
        "data-placeholder": placeholder,
        class:
          "chat-composer-editor min-h-6 max-h-32 overflow-y-auto text-sm leading-6 outline-none break-words [&_p]:m-0",
      },
      handleKeyDown: (view, event) => {
        if (event.isComposing || view.composing || event.keyCode === 229) return false;
        if (mentionRange.current && onMentionKey?.(event)) {
          event.preventDefault();
          if (event.key === "Escape") {
            dismissed.current = `${view.state.selection.from}:${view.state.selection.$from.parent.textBetween(0, view.state.selection.$from.parentOffset).match(/@([^@\n]*)$/)?.[1] ?? ""}`;
            mentionRange.current = null;
            onMention(null);
          }
          return true;
        }
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          onSend();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      onChange(serialize(editor.getJSON()));
      updateMention(editor);
    },
    onSelectionUpdate: ({ editor }) => updateMention(editor),
  });
  useEffect(() => {
    if (!editor) return;
    const update = () => { if (editor.isFocused) updateMention(editor); };
    const element = editor.view.dom;
    element.addEventListener("compositionend", update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      element.removeEventListener("compositionend", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  });
  useEffect(() => {
    if (editor && serialize(editor.getJSON()) !== value)
      editor.commands.setContent(content(value), false);
  }, [editor, value]);
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);
  useImperativeHandle(
    ref,
    () => ({
      insert(reference, replaceMention = false) {
        if (!editor) return;
        const { from } = editor.state.selection;
        const range = replaceMention ? mentionRange.current : null;
        const start = range?.from ?? from;
        const end = range?.to ?? from;
        mentionRange.current = null;
        editor
          .chain()
          .focus()
          .insertContentAt({ from: start, to: end }, [
            {
              type: "chatReference",
              attrs: {
                kind: reference.kind,
                id: reference.id,
                label: reference.label,
                viewType: reference.type ?? "",
                status: reference.status ?? "",
                projectLabel: reference.projectLabel ?? "",
              },
            },
            { type: "text", text: " " },
          ])
          .run();
        onMention(null);
      },
    }),
    [editor, onMention],
  );
  return <EditorContent editor={editor} className="flex-1 min-w-0 py-1" />;
});
